import { Controller } from "@hotwired/stimulus"
import DataTable from 'datatables.net-bs5'
import * as bootstrap from 'bootstrap'

// Connects to data-controller="movies-data-table"
export default class extends Controller {
  static targets = ['table', 'deleteModal', 'deleteMovieName', 'recommendModal', 'recommendModalTitle', 'recommendModalMessage', 'recommendMovieName', 'confirmToggleBtn', 'totpModal']
  static values = {
    isAdmin: Boolean,
    isQA: Boolean,
    isCreator: Boolean,
    isSuperAdmin: Boolean,
    isSuperAdminAccount: Boolean
  }
  
  connect() {
    this.initializeDataTable()
    this.setupEventListeners()
    this.deleteModal = new bootstrap.Modal(this.deleteModalTarget)
    this.recommendModal = new bootstrap.Modal(this.recommendModalTarget)
  }

  initializeDataTable() {
    this.dataTable = new DataTable(this.tableTarget, {
      'language': { 'processing': '<div id="loading"><i class="bi bi-arrow-repeat spin"></i></div>' },
      'responsive': true,
      'processing': true,
      'serverSide': true,
      'searching': false,
      'bLengthChange': false,
      'lengthMenu': 10,
      'columnDefs': [
        {
          'width': '10%',
          'name': 'created_at',
          'orderable': true,
          'className': 'text-center',
          'targets': 0
        },
        {
          'width': '10%',
          'name': 'publish',
          'orderable': true,
          'targets': 1,
          'className': 'text-center',
          'render': (data, type, row) => {
            if (data === 'true') {
              return `<i class="bi bi-circle-fill text-success"></i> Public`
            } else {
              return 'Draft'
            }
          }
        },
        {
          'width': '10%',
          'name': 'poster',
          'orderable': false,
          'targets': 2,
          'className': 'text-center',
          'render': (data, type, row) => {
            return `<a href="${data}"><img src="${data}" class="img-fluid" width="150" height="150" /></a>`
          }
        },
        {
          'width': '20%',
          'name': 'name',
          'className': 'text-center',
          'targets': 3
        },
        {
          'width': '10%',
          'name': 'account.name',
          'className': 'text-center',
          'targets': 4
        },
        {
          'width': '10%',
          'name': 'language.name',
          'className': 'text-center',
          'targets': 5
        },
        {
          'width': '10%',
          'name': 'genres.name',
          'orderable': false,
          'className': 'text-center',
          'targets': 6
        },
        {
          'width': '5%',
          'name': 'recommend',
          'orderable': false,
          'className': 'text-center',
          'targets': 7,
          'render': (data, type, row) => {
            if (data === 'true') {
              return `<i class="bi bi-check-lg text-success"></i>`
            } else {
              return `<i class="bi bi-x-lg text-danger"></i>`
            }
          }
        },
        {
          'width': '15%',
          'name': 'actions',
          'orderable': false,
          'className': 'text-center',
          'targets': 8,
          'render': (data, type, row) => {
            return this.renderActions(data, row)
          }
        }
      ],
      'ajax': {
        'url': '/movies/data_sources',
        'type': 'POST'
      }
    })
  }

  renderActions(data, row) {
    // edit action
    const editAction = `<a href="${data[1]}" class="me-2 icon-link" title="edit">
      <i class="bi bi-pencil-square text-warning"></i>
    </a>`

    // recommend action
    const movieId = data[4] ? data[4].split('/')[2] : null
    const movieName = row[3] || 'Unknown'
    const isRecommended = row[7] === 'true'
    const icon = `<i class="bi bi-star-fill toggle-recommend" data-movie-id="${movieId}" data-movie-name="${movieName}" data-current-status="${row[7]}"  style="cursor: pointer; ${isRecommended ? 'color: #ffc107;' : 'color: grey;'}" title="Click to toggle recommend status"></i>`
    const recommendAction = `<a href="#" class="me-2 icon-link" data-action="click->movies-data-table#toggleRecommend" data-movie-id="${movieId}" data-is-recommended="${isRecommended}" data-movie-name="${movieName}" data-current-status="${row[7]}">${icon}</a>`

    // delete action
    const deleteAction = `<a href="#" class="me-2 icon-link" data-action="click->movies-data-table#showDeleteModal" data-delete-url="${data[2]}" data-movie-name="${row[3]}" title="delete">
      <i class="bi bi-trash-fill text-danger"></i>
    </a>`

    if (this.isSuperAdminAccountValue && this.isSuperAdminValue) {
      return recommendAction
    } else if (this.isAdminValue) {
      return editAction + recommendAction + deleteAction
    } else {
      if (row[1] == 'false') {
        return editAction + deleteAction
      }
      return editAction
    }
  }

  setupEventListeners() {
    // Setup delete modal
    this.setupDeleteModal()
  }

  toggleRecommend(event) {
    event.preventDefault()
    const movieId = event.currentTarget.dataset.movieId
    const movieName = event.currentTarget.dataset.movieName
    const currentStatus = event.currentTarget.dataset.currentStatus
    const icon = event.currentTarget.querySelector('.toggle-recommend')
    const isCurrentlyRecommended = event.currentTarget.dataset.isRecommended === 'true'

    if (!movieId) {
      console.error('Movie ID not found')
      return
    }

    // Determine action and show appropriate confirmation dialog
    const action = isCurrentlyRecommended ? 'Remove from Recommend' : 'Add to Recommend'
    const message = isCurrentlyRecommended
      ? `Are you sure you want to remove this from "Recommended"?`
      : `Are you sure you want to add this to "Recommended"?`
    const buttonText = isCurrentlyRecommended ? 'Remove' : 'Add'

    // Update modal content
    this.recommendModalTitleTarget.textContent = action
    this.recommendModalMessageTarget.textContent = message
    this.recommendMovieNameTarget.textContent = movieName
    this.confirmToggleBtnTarget.textContent = buttonText

    // Set data attributes for the confirm button
    this.confirmToggleBtnTarget.dataset.movieId = movieId
    this.confirmToggleBtnTarget.dataset.currentStatus = currentStatus

    // Store reference to icon for later use
    this.currentToggleIcon = icon

    // Show modal
    this.recommendModal.show()
  }

  confirmToggleRecommend(event) {
    const movieId = event.currentTarget.dataset.movieId
    const currentStatus = event.currentTarget.dataset.currentStatus
    const icon = this.currentToggleIcon

    // Hide modal
    this.recommendModal.hide()

    // Show loading state
    icon.classList.remove('bi-star-fill')
    icon.classList.add('bi-arrow-repeat', 'spin')

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

    fetch(`/movies/${movieId}/toggle_recommend`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        window.location = data.redirect_url
        // Update icon based on new status
        if (data.recommend) {
          icon.classList.remove('bi-arrow-repeat', 'spin')
          icon.classList.add('bi-star-fill')
          icon.style.color = '#ffc107'
          icon.dataset.currentStatus = 'true'
        } else {
          icon.classList.remove('bi-arrow-repeat', 'spin')
          icon.classList.add('bi-star-fill')
          icon.style.color = 'grey'
          icon.dataset.currentStatus = 'false'
        }
      } else {
        // Revert to original state on error
        this.revertIconState(icon, currentStatus)
        alert('Error updating recommend status')
      }
    })
    .catch(error => {
      console.error('Error:', error)
      // Revert to original state on error
      this.revertIconState(icon, currentStatus)
      alert('Error updating recommend status')
    })
    .finally(() => {
      this.dataTable.ajax.reload()
    })
  }

  revertIconState(icon, currentStatus) {
    icon.classList.remove('bi-arrow-repeat', 'spin')
    icon.classList.add('bi-star-fill')
    if (currentStatus === 'true') {
      icon.style.color = '#ffc107'
    } else {
      icon.style.color = 'grey'
    }
  }

  showDeleteModal(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const deleteUrl = event.currentTarget.dataset.deleteUrl
    const movieName = event.currentTarget.dataset.movieName
    const row = event.currentTarget.closest('tr')

    this.deleteUrl = deleteUrl
    this.rowToDelete = row

    // Show delete modal
    if (this.deleteModalTarget) {
      this.deleteMovieNameTarget.textContent = movieName
      
      this.deleteModal.show()
    }
  }

  setupDeleteModal() {
    // Initialize delete modal variables
    this.deleteUrl = ''
    this.rowToDelete = null
  }

  cancelDelete() {
    this.deleteModal.hide()
  }

  confirmDelete() {
    if (!this.deleteUrl) return
    
    // Hide confirmation modal
    this.deleteModal.hide()
    
    // Check if TOTP verification is needed
    const currentAdminHasTotp = this.element.dataset.currentAdminTotpEnabled === 'true'
    
    if (currentAdminHasTotp) {
      this.showTotpModal()
    } else {
      this.sendDeleteRequest()
    }
  }

  sendDeleteRequest() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    
    fetch(this.deleteUrl, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (response.ok) {
        window.location.reload()
        return
        // Remove row from table
        if (this.rowToDelete) {
          this.rowToDelete.remove()
        }
        this.cancelDelete()
        this.dataTable.ajax.reload()
      } else {
        throw new Error('Delete failed')
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert(error.message || 'Delete failed')
    })
  }

  showTotpModal() {
    // Dispatch custom event to reusable TOTP modal
    const event = new CustomEvent('showTotpModal', {
      detail: {
        actionUrl: this.deleteUrl,
        actionMethod: 'DELETE',
        formData: {},
        rowToDelete: this.rowToDelete,
        dataTable: this.dataTable
      }
    })
    document.dispatchEvent(event)
  }

  disconnect() {
    if (this.dataTable) {
      this.dataTable.destroy()
    }
  }
}
